import BookingsTable from '@/components/admin/bookings';
import { FranchisorDetailed } from '@/components/admin/detailed';
import FranchisorBillingContactsAdminSection from '@/components/admin/detailed/FranchisorBillingContactsAdminSection';
import {
  Typography,
  Row,
  Flex,
  Button,
  MenuProps,
  Dropdown,
  Space,
  message,
  Modal,
} from 'antd';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Layout as BaseLayout } from 'antd';
import {
  Booking,
  BookingAdmin,
  BookingStatus,
  Consultant,
  Franchise,
} from '@/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAdminBookings } from '@/api/booking';
import { format } from 'date-fns';
import { DownOutlined } from '@ant-design/icons';
import { getAdminFranchisorById } from '@/api/franchisee';
import {
  deleteFranchise,
  downloadBookings,
  updateFranchise,
} from '@/api/admin';
import { useState } from 'react';
import { getBookingStatusLabel } from '@/utils/status';
import FranchisorsTable from '@/components/admin/franchisers/franchisors';
import { ROUTES } from '@/types/routes';
import { SvgIcon } from '@/components/icon';

const columns = [
  {
    title: 'ID',
    dataIndex: '_id',
    key: 'id',
    render: (text: string) => (
      <Typography.Link style={{ fontWeight: 500, color: '#1C8271' }}>
        {text}
      </Typography.Link>
    ),
  },
  {
    title: 'Consultant',
    dataIndex: 'consultant',
    key: 'consultant',
    render: (consultant: {
      _id: string;
      firstName: string;
      lastName: string;
    }) => (
      <Link
        style={{ fontWeight: 500, color: '#1C8271' }}
        to={`${ROUTES.ADMIN_CONSULTANTS}/${consultant._id}`}
      >
        {consultant.firstName} {consultant.lastName}
      </Link>
    ),
  },
  {
    title: 'Date',
    dataIndex: 'start',
    key: 'start',
    render: (date: string) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography.Text>
          {date ? format(new Date(date), 'd MMM, yyyy, h:mm a') : 'N/A'}
        </Typography.Text>
      </div>
    ),
  },
  {
    title: 'Marked As Completed',
    dataIndex: 'markedAsCompletedAt',
    key: 'markedAsCompletedAt',
    render: (
      date: string,
      record: { updatedAt: string; status: BookingStatus },
    ) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography.Text>
          {record.status === BookingStatus.COMPLETED && date
            ? format(new Date(date), 'd MMM, yyyy, h:mm a')
            : 'N/A'}
        </Typography.Text>
      </div>
    ),
  },
  {
    title: 'Total ($)',
    dataIndex: 'consultant',
    key: 'totalAmount',
    render: (consultant: Consultant, record: Booking) =>
      consultant?.rate ? consultant?.rate : 0,
  },
  {
    title: 'Rate',
    dataIndex: 'rating',
    key: 'rating',
    render: (rating: number) => {
      if (!rating) return '-';

      return (
        <Flex align="center" gap={6}>
          <Typography.Text>{rating}</Typography.Text>
          <SvgIcon type="goldStar" width={14} height={14} />
          <Typography.Link>Details</Typography.Link>
        </Flex>
      );
    },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status: 'pending' | 'declined' | 'completed' | 'future') => {
      const statusColors = {
        pending: '#FFA500',
        declined: '#F68B8B',
        completed: '#1C8271',
        future: '#2A4BC2',
      };
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: statusColors[status],
            }}
          />
          <Typography.Text>
            {getBookingStatusLabel(status)}
          </Typography.Text>
        </div>
      );
    },
  },
];

export default function AdminFranchisesDetailedPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const queryClient = useQueryClient();
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isHideModalOpen, setIsHideModalOpen] = useState(false);

  const navigate = useNavigate();
  const {
    data: franchise,
    isPending: isFranchisePending,
    isError: isFranchiseError,
  } = useQuery<Franchise | null, Error>({
    queryKey: ['franchisor', id],
    queryFn: () => getAdminFranchisorById(id || ''),
  });

  const { data } = useQuery<
    {
      total: number;
      page: number;
      limit: number;
      sessions: BookingAdmin[];
    },
    Error
  >({
    queryKey: ['bookings', searchParams.toString(), page, limit],
    queryFn: () =>
      getAdminBookings({
        startDate: searchParams.get('startDate') || '',
        endDate: searchParams.get('endDate') || '',
        search: searchParams.get('search') || '',
        franchiseId: id,
        page,
        limit,
      }),
  });

  const { mutateAsync: deactivateFranchise, isPending: isDeactivating } =
    useMutation({
      mutationKey: ['deactivateFranchise'],
      mutationFn: async (id: string) => {
        const res = await updateFranchise({
          data: {
            isDeactivated: franchise?.isDeactivated ? false : true,
          },
          id,
        });
        return res;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['franchisor', id],
        });
        queryClient.invalidateQueries({
          queryKey: ['franchisors'],
        });
        message.success(
          `Franchise ${
            franchise?.isDeactivated ? 'activated' : 'deactivated'
          } successfully`,
        );
      },
    });

  const { mutateAsync: hideFranchise, isPending: isHiding } = useMutation({
    mutationKey: ['hideFranchise'],
    mutationFn: async (id: string) => {
      const res = await updateFranchise({
        data: {
          status: franchise?.status ? false : true,
        },
        id,
      });
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['franchisor', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['franchisors'],
      });
      message.success(
        `Franchise ${franchise?.status ? 'hidden' : 'shown'} successfully`,
      );
    },
  });

  const { mutateAsync: deleteFranchiseMutation, isPending: isDeleting } =
    useMutation({
      mutationKey: ['deleteFranchise'],
      mutationFn: async (id: string) => {
        const res = await deleteFranchise(id);
        return res;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['franchisor', id],
        });
        queryClient.invalidateQueries({
          queryKey: ['franchisors'],
        });
        message.success(`Franchise deleted successfully`);
      },
    });
  const items: MenuProps['items'] = [
    {
      label: franchise?.isDeactivated
        ? 'Activate Franchise'
        : 'Deactivate Franchise',
      key: '1',
      disabled: isDeactivating,
    },
    {
      label: franchise?.status ? 'Hide Franchise' : 'Show Franchise',
      key: '2',
      disabled: isHiding,
    },
    {
      label: 'Delete Franchise',
      key: '3',
      danger: true,
      disabled: isDeleting,
    },
  ];

  const { mutateAsync: downloadBookingsMutate, isPending } = useMutation({
    mutationFn: () =>
      downloadBookings({
        startDate: searchParams.get('startDate') || '',
        endDate: searchParams.get('endDate') || '',
        search: searchParams.get('search') || '',
        franchiseId: id,
      }),
    // Single source of truth for HTTP-failure toasts. handleDownloadTable
    // catches the rejection but does NOT toast again — see TCX-540, where
    // both this callback AND the catch block were firing message.error,
    // producing two identical toasts per failed click.
    onError: (error: any) => {
      console.error('downloadBookings failed:', error);
      message.error(
        error?.message || 'Bookings download failed. Please try again.',
      );
    },
  });

  const handleDownloadTable = async () => {
    let result: { file?: string };
    try {
      result = await downloadBookingsMutate();
    } catch {
      // Mutation rejected — onError already surfaced the toast. Bail silently
      // so we don't double-toast.
      return;
    }

    const base64Response = result?.file;
    if (!base64Response || typeof base64Response !== 'string') {
      message.error('Bookings download failed: server returned an invalid file.');
      return;
    }

    try {
      const csvContent = atob(base64Response);
      const blob = new Blob([csvContent], { type: 'text/csv' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `bookings_${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      message.success('File downloaded successfully');
    } catch (error: any) {
      console.error('Bookings CSV save failed:', error);
      message.error(error?.message || 'Failed to save file.');
    }
  };

  const handleEditUser = async () => {
    navigate(`/control-panel/franchisors/${id}/edit`);
  };

  const handleDeactivateOk = async () => {
    if (!id) return;
    await deactivateFranchise(id);
    setIsDeactivateModalOpen(false);
  };

  const handleCancel = () => {
    setIsDeactivateModalOpen(false);
  };

  const handleDeleteOk = async () => {
    if (!id) return;
    await deleteFranchiseMutation(id);
    setIsDeleteModalOpen(false);
  };

  const handleHideOk = async () => {
    if (!id) return;
    await hideFranchise(id);
    setIsHideModalOpen(false);
  };

  const handleHideCancel = () => {
    setIsHideModalOpen(false);
  };
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '1') {
      setIsDeactivateModalOpen(true);
    }
    if (e.key === '2') {
      setIsHideModalOpen(true);
    }
    if (e.key === '3') {
      setIsDeleteModalOpen(true);
    }
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <>
      <Flex justify="space-between" align="center" gap={24}>
        <div>
          <Typography.Title
            level={2}
            style={{
              margin: '0 0 24px',
            }}
          >
            Franchise Details
          </Typography.Title>
          <Row className="tables-info">
            <Typography.Text style={{ color: '#9FA1AD', fontWeight: 500 }}>
              Franchise Management
            </Typography.Text>
            <span className="tables-divider" />
            <Typography.Text style={{ color: '#9FA1AD', fontWeight: 500 }}>
              Franchise Details
            </Typography.Text>
          </Row>
        </div>
        <Flex align="center" justify="center" gap={24}>
          <Dropdown menu={menuProps}>
            <Button
              type="primary"
              style={{
                backgroundColor: 'white',
                color: '#1C8271',
                boxShadow: 'none',
                height: '40px',
              }}
            >
              <Space>
                Actions
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
          <Button
            type="primary"
            className="admin-button-download-table"
            onClick={handleEditUser}
          >
            Edit Franchise
          </Button>
        </Flex>
      </Flex>
      <BaseLayout>
        <BaseLayout.Sider
          style={{ height: '100%', minHeight: '100vh' }}
          width={'25%'}
        >
          <FranchisorDetailed franchise={franchise as any} />
        </BaseLayout.Sider>
        <BaseLayout.Content style={{ padding: '0 16px' }}>
          <Flex vertical gap={24}>
            <FranchisorsTable franchisors={franchise?.users || []} />
            {/* TCX-875: read-only view of every billing contact email so
                support can verify where invoice emails go (blank/first-only
                was the TCX-873 gap). Lives in the wide content column, not
                the 25%-wide FranchisorDetailed sider — that sider is too
                narrow for a list of emails + status tags.
                isLoading/isError are passed explicitly (PR #805 finding 1):
                getAdminFranchisorById's error path always throws, so a
                failed or still-pending fetch leaves `franchise` undefined —
                indistinguishable from "no billing contacts" without this. */}
            <FranchisorBillingContactsAdminSection
              billingAddress={franchise?.billingAddress}
              billingRecipients={franchise?.billingRecipients}
              users={franchise?.users}
              stripeBillingEmailSynced={franchise?.stripeBillingEmailSynced}
              isLoading={isFranchisePending}
              isError={isFranchiseError}
            />
            <BookingsTable
              data={data?.sessions || []}
              total={data?.total || 0}
              columns={columns}
              isDetailed={true}
              page={page}
              setPage={setPage}
              limit={limit}
              setLimit={setLimit}
              downloadData={handleDownloadTable}
            />
          </Flex>
        </BaseLayout.Content>
      </BaseLayout>
      <Modal
        title={
          franchise?.isDeactivated
            ? 'Activate Franchise'
            : 'Deactivate Franchise'
        }
        open={isDeactivateModalOpen}
        onOk={handleDeactivateOk}
        onCancel={handleCancel}
      >
        <div className={'modal-section-line'}>
          <Typography.Text className="general-info-text">
            {` Are you sure you want to ${
              franchise?.isDeactivated ? 'activate' : 'deactivate'
            } this franchise?`}
          </Typography.Text>
        </div>
      </Modal>
      <Modal
        title={franchise?.status ? 'Hide Franchise' : 'Show Franchise'}
        open={isHideModalOpen}
        onOk={handleHideOk}
        onCancel={handleHideCancel}
      >
        <div className={'modal-section-line'}>
          <Typography.Text className="general-info-text">
            {` Are you sure you want to ${
              franchise?.status ? 'hide' : 'show'
            } this franchise?`}
          </Typography.Text>
        </div>
      </Modal>
      <Modal
        title="Delete Franchise?"
        open={isDeleteModalOpen}
        onOk={handleDeleteOk}
        onCancel={handleDeleteCancel}
      >
        <div className={'modal-section-line'}>
          <Typography.Text className="general-info-text">
            Are you sure you want to delete this franchise?
          </Typography.Text>
        </div>
      </Modal>
      <Modal
        title="Create User"
        open={isDeleteModalOpen}
        onOk={handleDeleteOk}
        onCancel={handleDeleteCancel}
      >
        <div className={'modal-section-line'}>
          <Typography.Text className="general-info-text">
            Are you sure you want to delete this franchise?
          </Typography.Text>
        </div>
      </Modal>
    </>
  );
}
